import styled from '@emotion/styled';
import { Button, message, Spin } from 'antd';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import dayjs from 'lib/dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { overseaTranslate } from 'services/api/overseaTranslate';
import { isNeedTranslateLanguage, langMap, NeedTranslateLanguage } from './Translation';
import { useChannel2Platform } from '../hooks/useChannel2Platform';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslateReport } from 'hooks/useTranslateReport';

export const SystemMessage = styled.div`
  height: 28px;
  line-height: 28px;
  padding: 0 18px;
  color: var(--fett-text-tertiary);
  background: rgba(0, 0, 0, 0.05);
  border-radius: 14px;
`;

const ReplyMessage = styled.div`
  width: 100%;
  white-space: pre-line;
  > div {
    padding: 16px;
    background: var(--fett-bg-panel);
    border-radius: 4px;
  }
  > button {
    display: none;
  }
  &:hover > button {
    display: block;
    margin-top: 8px;
  }
`;

const ReplyTranstion = styled.div`
  margin-top: 10px;
  padding-top: 10px;
  border-top: solid 1px #e7ebf0;
  color: var(--fett-text-tertiary);
`;

interface ReplyDetailProps {
  replyDetail: Review.ReplyDetail;
  channel: Review.Detail['channel'];
}

function ReplyDetail(props: ReplyDetailProps) {
  const { replyDetail, channel } = props;

  const {
    id,
    commentCreateTime,
    sellerName,
    sellerReply,
    transSellerReply = {},
    reviewType,
    status,
  } = replyDetail;

  const { t } = useTranslate();

  const [translationMap, setTranslationMap] = useState(transSellerReply);

  const [loading, setLoading] = useState(false);

  const systemLanguage = useSystemLanguageValue();
  const { isTikTokCommon } = useChannel2Platform()(channel);
  const translateReport = useTranslateReport();

  const onTranslate = useCallback(
    (language: NeedTranslateLanguage) => {
      if (!sellerReply) return;
      setLoading(true);

      return overseaTranslate(sellerReply, language, 'review')
        .then((res) => {
          setTranslationMap((map) => {
            return {
              ...map,
              [langMap[language]]: res,
            };
          });

          setLoading(false);

          translateReport(
            {
              id,
              flag: 2,
              transComment: {
                [langMap[language]]: res,
              },
            },
            'Review',
          );
        })
        .catch(() => {
          message.error(t('scene.reviewWorkspace.reply.error.translationFailed'));

          setLoading(false);
        });
    },
    [sellerReply, id, translateReport, t],
  );

  useEffect(() => {
    if (!isNeedTranslateLanguage(systemLanguage)) {
      return;
    }

    const lang = langMap[systemLanguage];

    if (translationMap[lang]) {
      return;
    }

    onTranslate(systemLanguage);
  }, [systemLanguage, translationMap, sellerReply, onTranslate]);

  const systemMsg = useMemo(() => {
    if (isTikTokCommon) {
      return (
        <SystemMessage>
          {dayjs(parseInt(commentCreateTime, 10)).format('YYYY-MM-DD HH:mm:ss')}&nbsp;
          {reviewType === 'FOLLOW_REPLY'
            ? t('scene.reviewWorkspace.detail.field.xRepliedToFollowUp', { x: sellerName })
            : t('scene.reviewWorkspace.detail.field.xRepliedToMain', { x: sellerName })}
        </SystemMessage>
      );
    }

    return (
      <SystemMessage>
        {dayjs(parseInt(commentCreateTime, 10)).format('YYYY-MM-DD HH:mm:ss')}&nbsp;
        {t('scene.reviewWorkspace.detail.field.xReply', { x: sellerName })}
      </SystemMessage>
    );
  }, [isTikTokCommon, sellerName, commentCreateTime, reviewType, t]);

  return (
    <>
      {systemMsg}
      <div className="flex items-center gap-2 w-full">
        {status === 1 && <Spin spinning />}
        {status === 3 && <ExclamationCircleOutlined className="!text-error" />}
        <ReplyMessage>
          <div>
            <div>{sellerReply}</div>
            {isNeedTranslateLanguage(systemLanguage) && translationMap[langMap[systemLanguage]] ? (
              <ReplyTranstion>{translationMap[langMap[systemLanguage]]}</ReplyTranstion>
            ) : null}
          </div>
          {isNeedTranslateLanguage(systemLanguage) && (
            <Button loading={loading} onClick={() => onTranslate(systemLanguage)}>
              {t('scene.reviewWorkspace.reply.action.translate')}
            </Button>
          )}
        </ReplyMessage>
      </div>
    </>
  );
}

export default ReplyDetail;
